'use client';

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Box from '@mui/material/Box';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { MouseEvent, ReactNode } from 'react';
import { composeSx } from '../../utils/styledUtils';
import { GlassButton } from '../GlassButton';
import { InlineTagChip } from '../InlineTagChip';
import { ToolCallChip, type ToolCallStatus } from '../ToolCallChip';

export type AstorRecommendationMode = 'APPROVE' | 'APPROVE_WITH_EXCEPTION' | 'REVIEW' | 'DENY' | 'PENDING';

export interface AstorRecommendationAction {
  id: string;
  label: ReactNode;
  variant: 'primary' | 'secondary' | 'link';
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
}

export interface AstorRecommendationToolCall {
  id: string;
  name: string;
  status?: ToolCallStatus;
  meta?: ReactNode;
}

export interface AstorRecommendationCardProps {
  mode: AstorRecommendationMode;
  /**
   * Confidence value 0–1. Rendered as a "conf NN%" chip beside the mode label.
   * Optional — omit when the surface only has aggregate (non-prediction) signal
   * and shouldn't fabricate a confidence number. When omitted, the chip is not
   * rendered.
   */
  confidence?: number;
  /** One-line headline (e.g. "Approve at $725,000 with experience exception"). */
  headline: ReactNode;
  /** One-paragraph rationale. */
  reasoning: ReactNode;
  /** Tool runs that fed the recommendation. */
  toolCalls?: AstorRecommendationToolCall[];
  /** Action buttons rendered in the right column. */
  actions?: AstorRecommendationAction[];
  /** Header meta — typically "5/7 tools complete · reasoned 7s ago". */
  meta?: ReactNode;
  /** Optional "Show full reasoning →" link / handler appended after reasoning. */
  onShowReasoning?: () => void;
  /** Sx on the outer Card. */
  sx?: SxProps<Theme>;
}

const MODE_LABELS: Record<AstorRecommendationMode, string> = {
  APPROVE: 'Astor recommends approve',
  APPROVE_WITH_EXCEPTION: 'Astor recommends',
  REVIEW: 'Astor flags for review',
  DENY: 'Astor recommends deny',
  PENDING: 'Astor is still reasoning',
};

const MODE_TONE: Record<AstorRecommendationMode, 'success' | 'warning' | 'error' | 'primary' | 'neutral'> = {
  APPROVE: 'success',
  APPROVE_WITH_EXCEPTION: 'success',
  REVIEW: 'warning',
  DENY: 'error',
  PENDING: 'neutral',
};

function actionColor(variant: AstorRecommendationAction['variant']): 'primary' | 'inherit' {
  return variant === 'primary' ? 'primary' : 'inherit';
}

function actionMuiVariant(variant: AstorRecommendationAction['variant']): 'contained' | 'outlined' | 'text' {
  if (variant === 'primary') return 'contained';
  if (variant === 'secondary') return 'outlined';
  return 'text';
}

export function AstorRecommendationCard({
  mode,
  confidence,
  headline,
  reasoning,
  toolCalls = [],
  actions = [],
  meta,
  onShowReasoning,
  sx,
}: AstorRecommendationCardProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const tone = MODE_TONE[mode];

  const accentColor = tone === 'neutral' ? theme.palette.text.secondary : theme.palette[tone].main;

  const safeConfidence = typeof confidence === 'number' ? Math.round(Math.max(0, Math.min(1, confidence)) * 100) : null;

  return (
    <Box
      sx={composeSx(
        {
          position: 'relative',
          display: 'flex',
          gap: 2.5,
          p: 2.5,
          borderRadius: 1.5,
          backgroundColor: alpha(accentColor, isDark ? 0.08 : 0.06),
          border: `1px solid ${alpha(accentColor, isDark ? 0.32 : 0.24)}`,
        },
        sx,
      )}
    >
      <Box
        aria-hidden
        sx={{
          flexShrink: 0,
          width: 32,
          height: 32,
          borderRadius: '50%',
          backgroundColor: alpha(accentColor, 0.18),
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: accentColor,
        }}
      >
        <AutoAwesomeIcon fontSize="small" />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mb: 1 }}>
          <InlineTagChip label={MODE_LABELS[mode]} severity={tone} />
          {safeConfidence !== null && <InlineTagChip label={`conf ${safeConfidence}%`} severity="neutral" />}
          {meta && (
            <Typography variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>
              {meta}
            </Typography>
          )}
        </Box>

        <Typography
          variant="h6"
          component="div"
          sx={{
            color: 'text.primary',
            fontWeight: 600,
            lineHeight: 1.35,
            mb: 1,
          }}
        >
          {headline}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: 'text.primary',
            lineHeight: 1.6,
            mb: 1.5,
          }}
        >
          {reasoning}
          {onShowReasoning && (
            <Box
              component="button"
              type="button"
              onClick={onShowReasoning}
              sx={{
                ml: 0.5,
                background: 'none',
                border: 'none',
                p: 0,
                color: accentColor,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: 'inherit',
                lineHeight: 'inherit',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Show full reasoning →
            </Box>
          )}
        </Typography>

        {toolCalls.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {toolCalls.map((call) => (
              <ToolCallChip key={call.id} toolName={call.name} status={call.status ?? 'done'} meta={call.meta} />
            ))}
          </Box>
        )}
      </Box>

      {actions.length > 0 && (
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.75,
            alignItems: 'stretch',
            minWidth: 160,
          }}
        >
          {actions.map((action) => (
            <GlassButton
              key={action.id}
              variant={actionMuiVariant(action.variant)}
              color={actionColor(action.variant)}
              onClick={action.onClick}
              disabled={action.disabled}
              size="medium"
              fullWidth
            >
              {action.label}
            </GlassButton>
          ))}
        </Box>
      )}
    </Box>
  );
}

AstorRecommendationCard.displayName = 'AstorRecommendationCard';

export default AstorRecommendationCard;
